import {
  CompensationStructure,
  PayrollDraftEmployeeCompensationDetails,
} from 'api/gql-utils/employeeOnboardingGql/types';
import {
  customSalaryStructureDefaultComponentsWithoutNps,
  customSalaryStructureDefaultComponentsWithNps,
  SalaryStructureComponent,
  TaxableValue,
} from '../constants';
import { useState } from 'react';
import useFeature from 'hooks/useFeature';

const generateUniqueId = (): string => {
  const timestamp = Date.now();
  return `custom-${timestamp}`;
};

const emptyComponent: SalaryStructureComponent = {
  id: '',
  name: '',
  isNameEditable: true,
  amount: '',
  taxable: 'no',
  isTaxableEditable: true,
  isRemovable: true,
  type: 'customAllowance',
};

const getInitialValue = (
  earnings: PayrollDraftEmployeeCompensationDetails['earnings'] | undefined,
  isNpsFlagEnabled: boolean,
): SalaryStructureComponent[] => {
  const customSalaryStructureDefaultComponents = isNpsFlagEnabled
    ? customSalaryStructureDefaultComponentsWithNps
    : customSalaryStructureDefaultComponentsWithoutNps;
  if (!earnings) {
    return [
      ...customSalaryStructureDefaultComponents,
      {
        ...emptyComponent,
        id: generateUniqueId(),
      },
    ];
  }
  const components: SalaryStructureComponent[] = customSalaryStructureDefaultComponents
    .filter(({ id }) => {
      return earnings?.[id as keyof CompensationStructure] !== undefined;
    })
    .map((component) => ({
      ...component,
      amount: earnings?.[component.id as keyof CompensationStructure]?.toString() ?? '0',
    }));

  const customAllowances: SalaryStructureComponent[] =
    earnings.customAllowances?.map((allowance) => ({
      id: allowance.name,
      name: allowance.name,
      isNameEditable: true,
      amount: allowance.value.toString(),
      taxable: (allowance.taxable || 'no') as TaxableValue,
      isTaxableEditable: true,
      isRemovable: true,
      type: 'customAllowance',
    })) || [];

  const allComponents = [...components, ...customAllowances];

  return [
    ...allComponents,
    {
      ...emptyComponent,
      id: generateUniqueId(),
    },
  ];
};

export const useEmployeeCustomSalaryStructure = ({
  compensationDetails,
}: {
  compensationDetails: PayrollDraftEmployeeCompensationDetails | undefined;
}) => {
  const { isNpsFlagEnabled } = useFeature('npsFlag');
  const [components, setComponents] = useState<SalaryStructureComponent[]>(
    getInitialValue(compensationDetails?.earnings, isNpsFlagEnabled),
  );

  const addNewComponent = () => {
    setComponents((prevComponents) => [
      ...prevComponents,
      {
        ...emptyComponent,
        id: generateUniqueId(),
      },
    ]);
  };

  const removeComponent = ({ id }: { id: string }) => {
    setComponents((prevComponents) => prevComponents.filter((component) => component.id !== id));
  };

  const updateComponent = ({
    id,
    component,
  }: {
    id: string;
    component: Partial<SalaryStructureComponent>;
  }) => {
    setComponents((prevComponents) =>
      prevComponents.map((prevComponent) =>
        prevComponent.id === id ? { ...prevComponent, ...component } : prevComponent,
      ),
    );
  };

  return { components, addNewComponent, removeComponent, updateComponent };
};
